import { styled } from "@mui/material/styles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import React from "react";
import { Box, Spinner, Text } from "@origin-digital/ods-core";
import { useFeedContext } from "src/context/Feed";
import { useTheme } from "src/hooks";
import { SortDirectionEnum } from "../Premises/types";
import { DraftConnectionTypeIconWithHoverTip } from "../Icons";
import { updateToDiscard } from "../Premises/helpers";
import { DraftsTableRow, SortByEnum } from "./types";
import { mapDraftsTableRowKeyToSortByEnum } from "./helpers";
import { StatusIcon } from "./Icons";
import { useDraftsNavigation } from "./hooks";
import { DraftsContextMenu } from "./DraftsContextMenu";
import { DeleteDraftDialogProvider } from "./DeleteDraftDialog";

const StyledTableCell = styled(TableCell)(() => ({
  textAlign: "left",
  verticalAlign: "middle",
}));

const StyledTableHeadCell = styled(StyledTableCell)(() => ({
  backgroundColor: "#f8f8f8",
}));

const StyledTableRow = styled(TableRow)(() => ({
  cursor: "pointer",
  height: 64,
}));

type ColumnConfig = {
  id: keyof DraftsTableRow;
  label: string;
  width?: string;
  flex?: number;
  disableSorting?: boolean;
};

const columns: ColumnConfig[] = [
  { id: "connectionType", label: "Type", width: "40px" },
  { id: "address", label: "Address", flex: 1 },
  { id: "id", label: "ID", width: "80px" },
  { id: "lastEdited", label: "Last edited", width: "100px" },
  { id: "statusText", label: "Draft status", flex: 1 },
];

export const DraftsTable: React.FC = () => {
  const {
    loading,
    rows,
    sortConfig,
    setSortConfig,
    scrollableProps,
    doFetch,
    setPage,
    setScrollPosition,
  } = useFeedContext();
  const theme = useTheme();
  const handleSort = (key: keyof DraftsTableRow) => {
    const isAsc =
      sortConfig &&
      sortConfig.key === mapDraftsTableRowKeyToSortByEnum(key) &&
      sortConfig.direction === SortDirectionEnum.ASC;
    const sortBy = mapDraftsTableRowKeyToSortByEnum(key) ?? SortByEnum.UPDATED;
    const sortDirection = isAsc
      ? SortDirectionEnum.DESC
      : SortDirectionEnum.ASC;
    setSortConfig({
      key: sortBy,
      direction: sortDirection,
    });
    setPage(0);
    setScrollPosition(0);
    doFetch({
      incremental: false,
      pageToFetch: 0,
      sortConfigToFetch: {
        key: sortBy,
        direction: sortDirection,
      },
    });
  };

  const reFetchDrafts = () => {
    doFetch({
      incremental: false,
      pageToFetch: 0,
      sortConfigToFetch: {
        key: sortConfig?.key,
        direction: sortConfig?.direction,
      },
    });
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        maxHeight: 700,
        maxWidth: 1440, // Set maximum width
        overflow: "auto",
        margin: "0 auto", // Center the table container
      }}
      onScroll={scrollableProps.onContainerScroll}
      ref={scrollableProps.containerRef}
    >
      <Table
        sx={{ minWidth: 650, tableLayout: "fixed" }}
        size="small"
        aria-label="drafts table"
        stickyHeader
      >
        <TableHead sx={{ backgroundColor: "#F1F1F1" }}>
          <StyledTableRow>
            {columns.map((column) => {
              const isCurrentCol =
                sortConfig?.key === mapDraftsTableRowKeyToSortByEnum(column.id);
              return (
                <StyledTableHeadCell
                  key={column.id}
                  sx={{
                    width: column.width,
                    flex: column.flex,
                    minWidth: column.flex ? "100px" : undefined,
                  }}
                >
                  {column.disableSorting ? (
                    <Text variant="body-small" weight="medium">
                      {column.label}
                    </Text>
                  ) : (
                    <TableSortLabel
                      active={isCurrentCol}
                      direction={
                        isCurrentCol
                          ? (sortConfig.direction.toLowerCase() as
                              | "desc"
                              | "asc")
                          : "asc"
                      }
                      onClick={() => handleSort(column.id)}
                      sx={{
                        "&.Mui-active .MuiTableSortLabel-icon": {
                          color: theme.colors.critical,
                        },
                      }}
                    >
                      <Text
                        variant="body-small"
                        tone={isCurrentCol ? "critical" : undefined}
                        weight="medium"
                      >
                        {column.label}
                      </Text>
                    </TableSortLabel>
                  )}
                </StyledTableHeadCell>
              );
            })}
            <StyledTableHeadCell align="right" sx={{ width: "40px" }} />
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {rows &&
            rows.map((row, index) => (
              <EnhancedRow
                key={index}
                row={row}
                reFetchDrafts={reFetchDrafts}
                tableColumns={columns}
              />
            ))}
        </TableBody>
      </Table>
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginY="medium"
        >
          <Spinner />
        </Box>
      )}
    </TableContainer>
  );
};

const Row = ({
  row,
  reFetchDrafts,
  tableColumns,
}: {
  row: DraftsTableRow;
  reFetchDrafts: () => void;
  tableColumns: ColumnConfig[];
}) => {
  const onDeleteDraftConfirmed = () => {
    updateToDiscard(row.id, reFetchDrafts);
  };

  const { handleOnClick } = useDraftsNavigation(row);

  return (
    <>
      <StyledTableRow onClick={handleOnClick}>
        {tableColumns.map((column) => (
          <StyledTableCell
            key={column.id}
            sx={{
              width: column.width,
              flex: column.flex,
              minWidth: column.flex ? "100px" : undefined,
            }}
            onClick={(e) => column.id === "id" && e.stopPropagation()}
          >
            {column.id === "connectionType" && (
              <DraftConnectionTypeIconWithHoverTip
                type={row.connectionType}
                state={row.state}
                channel={row.requestChannel}
                requestType={row.requestType}
                serviceType={row.serviceType}
              />
            )}
            {column.id === "address" && (
              <Text variant="body-small">{row.address}</Text>
            )}
            {column.id === "id" && <Text variant="body-small">{row.id}</Text>}
            {column.id === "lastEdited" && (
              <Text variant="body-small">{row.lastEdited || "-"}</Text>
            )}
            {column.id === "statusText" && (
              <Box display="flex" alignItems="center">
                <Box marginTop="xxsmall" marginRight="xsmall">
                  <StatusIcon status={row.status} />
                </Box>
                <Text
                  tone={row.status === "error" ? "critical" : "neutral"}
                  variant="body-small"
                >
                  {row.statusText}
                </Text>
              </Box>
            )}
          </StyledTableCell>
        ))}
        <StyledTableCell padding="normal" sx={{ width: "40px" }}>
          <DraftsContextMenu
            row={row}
            onDeleteDraftConfirmed={onDeleteDraftConfirmed}
          />
        </StyledTableCell>
      </StyledTableRow>
    </>
  );
};

const EnhancedRow = (props: React.ComponentProps<typeof Row>) => (
  <DeleteDraftDialogProvider>
    <Row {...props} />
  </DeleteDraftDialogProvider>
);
